import { type HeadersFunction, type LoaderFunctionArgs, json } from '@remix-run/node'
import { Link, useLoaderData, useMatches, useNavigate } from '@remix-run/react'
import { MenuIcon } from 'lucide-react'
import * as React from 'react'
import Image from '#app/components/image.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { CRAFT_QUERY_KEYS } from '#app/utils/graphql.server.ts'
import { useKeyPress } from '#app/utils/misc'
import { type SliderCard } from '../../../types/graphql.js'
import { type PortfolioListItem } from '../../../types/graphql.page.ts'
import { type RouteHandle } from '../../../types/index.ts'

export const handle: RouteHandle & { id: string } = {
	id: 'portfolio-images',
	restoreScroll: false,
}

export async function loader({ params, request }: LoaderFunctionArgs) {
	const url = new URL(request.url)
	const noCache = Object.entries(Object.fromEntries(url.searchParams)).filter(
		([key]) => CRAFT_QUERY_KEYS.includes(key),
	).length
	const portfolioName = params.name || ''
	const imageIdx = Number(params.img) || 1
	const parent = `/portfolio/${portfolioName}`

	return json(
		{ portfolioName, imageIdx, parent },
		{
			headers: {
				'Cache-Control': noCache
					? 'max-age=0, s-maxage=0'
					: 'max-age=60, s-maxage=300',
			},
		},
	)
}

export let headers: HeadersFunction = ({ loaderHeaders }) => {
	return { 'Cache-Control': loaderHeaders.get('Cache-Control') || '' }
}

type Gallery = { gallery: Array<SliderCard>; subTitle: string }

export default function PortfolioNameImageRoute() {
	const { parent, imageIdx } = useLoaderData<typeof loader>()
	const [open, setOpen] = React.useState(false)
	const navigate = useNavigate()
	const matches = useMatches()
	const portfolioMatch = matches.find(
		match =>
			(match.handle as RouteHandle | undefined)?.id === 'portfolio-entry',
	)
	let title = 'Portfolio'
	let currentCard: PortfolioListItem | undefined
	let next: string | undefined
	let prev: string | undefined
	if (portfolioMatch) {
		const data = portfolioMatch.data as Gallery
		const currentCardIdx = imageIdx - 1
		currentCard = data.gallery[currentCardIdx]
		next = `${parent}/gallery/${
			((currentCardIdx + 1) % data.gallery.length) + 1
		}`
		prev = `${parent}/gallery/${
			currentCardIdx === 0
				? data.gallery.length
				: ((currentCardIdx - 1) % data.gallery.length) + 1
		}`
		title = data.subTitle || ''
	}

	const dismiss = React.useCallback(
		() => navigate('..', { state: { doNotScroll: true } }),
		[navigate],
	)
	const escaped = useKeyPress('Escape')
	const right = useKeyPress('ArrowRight')
	const left = useKeyPress('ArrowLeft')

	React.useEffect(() => {
		if (escaped) {
			dismiss()
		}
		if (right && next) {
			navigate(next)
		}
		if (left && prev) {
			navigate(prev)
		}
	}, [dismiss, escaped, left, navigate, next, prev, right])

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button className="pl-5 lg:hidden" aria-label="Menu">
					<MenuIcon className="h-8 w-8 fill-white" />
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader className="heading">
					<DialogTitle>
						<div>{title}</div>
					</DialogTitle>
					<div className="flex flex-row place-content-between">
						{prev && <Link to={prev}>&lt;&lt; Prev</Link>}
						{next && <Link to={next}>Next &gt;&gt;</Link>}
					</div>
				</DialogHeader>
				<div className="description">
					{currentCard?.image[0] && (
						<Image
							img={currentCard.image[0]}
							className="max-h-[calc(100vh*0.7)]"
						/>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
